import React, { FC, ChangeEvent, FormEvent, useContext, useState } from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import { i18nFunct } from '@lucid/gatsby-theme-lucid';
import LayoutContext, {
  LayoutContextProps
} from '../../contexts/layoutContext';
import { useSiteMetadata } from '../../hooks/use-site-metadata';

import './email-only.scss';
import { registerUserEmailOnly } from '../../utils/registration';

export enum EmailOnlyThemes {
  Default = '',
  Retro = 'retro'
}

export enum EmailOnlyLayouts {
  Default = '',
  Compact = 'compact',
  Cushy = 'cushy',
  Stacked = 'stacked',
  Minimal = 'minimal',
  Horizontal = 'horizontal',
  Even = 'even',
  Uneven = 'uneven',
  SsoFull = 'sso-full'
}

export enum EmailOnlyBreakPoints {
  Always = 'always',
  MaxWidth = 'max-width',
  Computer = 'computer',
  TabletLandscape = 'tablet-landscape',
  Tablet = 'tablet',
  SmallTablet = 'small-tablet',
  Mobile = 'mobile',
  BabyMobile = 'baby-mobile'
}

export enum EmailOnlySizing {
  Default = '',
  Compact = 'compact',
  Cushy = 'cushy'
}

interface EmailOnly {
  buttonText?: string;
  googleButton?: string;
  bounceDetection?: boolean;
  theme?: EmailOnlyThemes;
  layout?: EmailOnlyLayouts;
  sizing?: EmailOnlySizing;
  stackAt?: EmailOnlyBreakPoints;
  pageType?: string;
  wideBreak?: boolean;
  hideSSO?: boolean;
  hideOr?: boolean;
  isVisio?: boolean;
  returnUrl?: string;
  checkForService?: boolean;
  placeholder?: string;
  orTextColor?: string;
}

const EmailOnly: FC<EmailOnly> = ({
  pageType = '',
  bounceDetection = false,
  buttonText = '',
  googleButton = '',
  theme = EmailOnlyThemes.Default,
  layout = EmailOnlyLayouts.Default,
  sizing = EmailOnlySizing.Default,
  wideBreak = false,
  hideSSO = false,
  hideOr = false,
  stackAt = EmailOnlyBreakPoints.SmallTablet,
  isVisio = false,
  returnUrl = '/users/level/newUser',
  checkForService = false,
  placeholder = '',
  orTextColor = ''
}) => {
  const [email, setEmail] = useState('');
  const [isError, setIsError] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const { productUrl } = useSiteMetadata();
  const {
    language: { urlLangPrefix, i18nKey },
    modal: { openModal }
  } = useContext<LayoutContextProps>(LayoutContext);

  const { i18nData } = useStaticQuery(
    graphql`
      {
        i18nData: allFile(filter: { sourceInstanceName: { eq: "i18n" } }) {
          edges {
            node {
              name
              childSrcJson {
                mktg_bounce_detection_rejection
                mktg_email_only_registering
                mktg_email_only_placeholder
                mktg_nav_sign_up_free
                mktg_email_only_or
                mktg_email_only_sign_up_with
                mktg_email_only_or_sign_up_with
                mktg_ct_all_default_form_heading
                mktg_privacy_form
                mktg_email_only_enterprise_msg
              }
            }
          }
        }
      }
    `
  );

  const i18n = i18nFunct(i18nData, i18nKey);
  const [buttonCopy, setButtonCopy] = useState(
    buttonText || i18n('mktg-nav-sign-up-free')
  );
  const registering = i18n('mktg-email-only-registering');

  const isRetro = theme === EmailOnlyThemes.Retro;

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
  };

  const sendBounceMetric = () => {
    if (bounceDetection) {
      if (window.lucid && window.lucid.kissMetrics) {
        window.lucid.kissMetrics.recordEvent('Bounce registered');
      }
    }
  };

  const submitEmailOnly = async (e: FormEvent<HTMLFormElement>) => {
    const emailReg = /^[a-zA-Z0-9.!#$%&'*+\/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
    const visioQueryParam = '?type=visio';
    const timeoutMs = 6000;
    const failedUrl = `${productUrl}/${urlLangPrefix}users/registerLevel`;

    e.preventDefault();

    const emailOnlyData = {
      email,
      finalizeNow: true,
      seenTos: true,
      bounceDetection,
      discoveryPage: false
    };

    if (pageType === 'discovery') {
      emailOnlyData.discoveryPage = true;
      emailOnlyData.bounceDetection = false;
    }

    if (email.length === 0) {
      setIsError(true);
    }

    if (email.match(emailReg)) {
      setButtonCopy(registering);
      setIsDisabled(true);

      try {
        const {
          isEnterpriseUser,
          redirectUrl,
          response
        } = await registerUserEmailOnly({
          data: emailOnlyData,
          urlLangPrefix,
          productUrl,
          checkForService
        });

        if (response.status === 200) {
          sendBounceMetric();

          if (checkForService) {
            if (!isEnterpriseUser) {
              // regular registration, forward to pricing page
              window.location.assign(
                `${productUrl}/${urlLangPrefix}users/level/newUser`
              );
            } else {
              // they are confirmed enterprise, show modal and redirect after delay
              openModal(
                <div className="after-registration__content">
                  <h3 className="after-registration__title">
                    {i18n('mktg-ct-all-default-form-heading')}
                  </h3>
                  <p className="after-registration__text">
                    {i18n('mktg-email-only-enterprise-msg')}
                  </p>
                </div>
              );

              setTimeout(() => {
                window.location.assign(redirectUrl);
              }, timeoutMs);
            }
          } else {
            let url = redirectUrl;
            const paramSeparator = url.match(/\?./) ? '&' : '?';
            if (returnUrl.includes('documents')) {
              url += `${paramSeparator}return_url=${returnUrl}&canInterruptWithPayments=1`;
            } else if (isVisio) {
              url += visioQueryParam;
            }
            window.location.assign(url);
          }
        } else {
          let url = failedUrl;
          url += isVisio ? visioQueryParam : '';
          window.location.assign(url);
        }
      } catch (e) {
        console.error(e);
        window.location.assign(failedUrl);
      }
    } else if (bounceDetection) {
      setIsError(true);
    }
  };

  const block = 'eo-form';

  return (
    <form
      method="post"
      className={`js-${block} ${block} ${
        theme ? `${block}--theme-${theme}` : ''
      } ${layout ? `${block}--layout-${layout}` : ''} ${
        wideBreak ? `${block}--wide-break` : ''
      }   ${stackAt ? `${block}--stack-at-${stackAt}` : ''}  ${
        sizing ? `${block}--sizing-${sizing}` : ``
      } ${hideSSO ? `${block}--hide-sso` : ''} `}
      onSubmit={submitEmailOnly}
      noValidate
    >
      <fieldset className={`${block}__fieldset`} disabled={isDisabled}>
        <div className={`${block}__fieldset-wrapper`}>
          <input
            className={`${block}__email ${
              isError ? `${block}__email--error` : ''
            }`}
            type="email"
            name="email"
            autoComplete="email"
            placeholder={placeholder || i18n('mktg-email-only-placeholder')}
            maxLength={80}
            value={email}
            onChange={handleChange}
            autoFocus={bounceDetection}
          />
          {bounceDetection && isError && (
            <p className={`${block}__error`}>
              <span className={`${block}__error-icon`}>!</span>
              {i18n('mktg-bounce-detection-rejection')}
            </p>
          )}
          <button
            className={`${block}__submit  button  chart  chart--primary  ${
              isRetro ? 'chart--retro' : ''
            }`}
            type="submit"
            name="submit"
          >
            {buttonCopy}
          </button>
          {!hideOr && !hideSSO && (
            <div
              className={`${block}__or-text ${
                orTextColor === 'white' ? `${block}__or-text--white` : ''
              }`}
            >
              {i18n('mktg-email-only-or')}
            </div>
          )}
          {!hideSSO && (
            <a
              className={`${block}__google  button  chart  chart--secondary  ${
                isRetro ? `${block}__google--retro` : ''
              }  google-standardized`}
              href={`${productUrl}/${urlLangPrefix}users/googleFederatedLogin?level_id=free&seenTos=1&return_url=${encodeURIComponent(
                returnUrl
              )}`}
            >
              <img
                className={`${block}__google-mark`}
                alt="Google Mark"
                src="https://d2slcw3kip6qmk.cloudfront.net/marketing/pages/chart/logos/btn_google_light_normal.svg"
              />
              <span className={`${block}__google-text`}>
                {googleButton ||
                  i18n('mktg-email-only-sign-up-with', {
                    service: 'Google'
                  })}
              </span>
            </a>
          )}
          <p
            className={`${block}__tos ${
              hideSSO ? `${block}__tos--full-width` : ''
            }`}
            dangerouslySetInnerHTML={{
              __html: i18n('mktg-privacy-form', {}, [
                `<a href="/pages/tos" class="${block}__link">{}</a>`,
                `<a href="/pages/privacy" class="${block}__link">{}</a>`
              ])
            }}
          />
        </div>
      </fieldset>
    </form>
  );
};

export default EmailOnly;
